/**
 * Kontrakt migawki rozpoznania starej bazy (ADR-0110, faza 27).
 *
 * Mieszka w paczce współdzielonej, bo mają go DWIE strony: program działający u klienta
 * (składa migawkę) i serwer (waliduje ją i buduje z niej ekran mapowania). Dwie kopie tego
 * samego kształtu rozjechałyby się przy pierwszej zmianie, a objawem byłoby ciche gubienie
 * pól u klienta, który ma starszą wersję programu.
 *
 * To jedyny styk między programem działającym u klienta a ekranem mapowania: program
 * odczytuje schemat u siebie i odsyła go w tym kształcie, my go zapisujemy i z niego
 * budujemy lewą kolumnę mapowania. Dlatego kształt jest tu zwalidowany, a nie przyjmowany
 * na wiarę — program stoi na cudzym serwerze i bywa w starszej wersji niż serwer.
 *
 * DLACZEGO JEDNA LISTA PÓL NA KARTOTEKĘ, A NIE DWIE: pola rdzeniowe (stałe kolumny kartoteki)
 * i dodatkowe (dokładane przez klienta w czasie działania) różnią się pochodzeniem, ale dla
 * mapowania są tym samym — czymś, co można wskazać po lewej stronie. Rozdzielenie ich
 * zmuszałoby ekran i silnik do obsługi dwóch list, które i tak zaraz trzeba złączyć.
 *
 * CZEGO TU NIE MA ŚWIADOMIE: struktury tabeli pól dodatkowych. W rozpoznanej bazie miała ona
 * 111 kolumn, z czego 78 po dawno skasowanych polach — część o nazwach sugerujących hasła.
 * Zestaw pól bierzemy WYŁĄCZNIE z tabeli definicji starego systemu; kolumny bez definicji
 * nie istnieją dla migratora.
 */

import { z } from 'zod';

/**
 * Typy pól w starym WF, sprowadzone do tego, co ma znaczenie przy przenoszeniu.
 *
 * `button` to pole bez kolumny — w starym systemie rysuje przycisk uruchamiający procedurę.
 * Nie da się go przenieść i nie powinno trafić na listę wyboru, ale musi przyjść w migawce,
 * żeby wdrożeniowiec nie szukał pola, które widzi w aplikacji klienta, a którego u nas nie ma.
 */
export const LEGACY_FIELD_TYPES = [
  'string',
  'int',
  'decimal',
  'boolean',
  'datetime',
  'reference',
  'button',
  'unknown',
] as const;

export type LegacyFieldType = (typeof LEGACY_FIELD_TYPES)[number];

/** Kartoteki objęte rozpoznaniem — te same wartości co kolumna `entity` w mapowaniach. */
export const LEGACY_ENTITIES = ['contractor', 'employee', 'contractor_contact'] as const;
export type LegacyEntity = (typeof LEGACY_ENTITIES)[number];

const fieldSchema = z.object({
  /** 'core' = stała kolumna kartoteki, 'ext' = pole dodane przez klienta. */
  origin: z.enum(['core', 'ext']),
  /** Nazwa kolumny w bazie klienta. Bywa ze spacjami i polskimi znakami. */
  propertyName: z.string().min(1).max(128),
  /** Etykieta, którą klient widzi w swojej aplikacji — to ona ma być na ekranie mapowania. */
  displayName: z.string().max(255),
  type: z.enum(LEGACY_FIELD_TYPES),
  /** Dla tekstów: pojemność kolumny. Pozwala ostrzec, że nasze pole jest węższe. */
  maxLength: z.number().int().positive().nullable(),
  /** Kod słownika, gdy pole jest słownikowe. Wartość w kolumnie to TEKST tego kodu. */
  dictionaryCode: z.string().max(100).nullable(),
  /**
   * Gdy pole wskazuje na inny rekord — na jaką kartotekę. W źródle leży tam NUMER rekordu,
   * więc przy przenoszeniu trzeba go przetłumaczyć na nasz identyfikator (drugi przebieg).
   */
  referenceTarget: z.enum([...LEGACY_ENTITIES, 'project']).nullable(),
});

export type LegacySourceField = z.infer<typeof fieldSchema>;

const dictionarySchema = z.object({
  code: z.string().max(100),
  name: z.string().max(255),
  /** Czy aplikacja klienta pokazuje kod, czy nazwę — wpływa na to, co leży w kolumnie. */
  useCodes: z.boolean(),
  /**
   * Słownik wyliczany procedurą po stronie klienta. Pozycji nie znamy z góry i nie da się
   * ich wylistować bez uruchomienia tej procedury — mapowanie wartości robi się tu ręcznie.
   */
  external: z.boolean(),
  items: z.array(z.object({ code: z.string().max(100), name: z.string().max(255) })),
});

export const legacySourceSchemaPayloadSchema = z.object({
  /** Wersja kontraktu — gdy program jest starszy niż serwer, chcemy to zobaczyć od razu. */
  version: z.literal(1),
  database: z.object({
    name: z.string().max(128),
    serverVersion: z.string().max(255).optional(),
  }),
  /**
   * Numery typów obiektów odczytane PO NAZWIE z mapy typów danej bazy. Różnią się między
   * klientami, a bez nich zapisany rekord nie pojawi się na listach w starej aplikacji.
   * Puste = w tej bazie nie ma takiego typu (zdarza się dla typów bazowych).
   */
  objectTypes: z.object({
    contractor: z.number().int().nullable(),
    employee: z.number().int().nullable(),
    contractor_contact: z.number().int().nullable(),
  }),
  companies: z.array(
    z.object({
      oid: z.number().int(),
      code: z.string().max(100).nullable(),
      name: z.string().max(255),
      /** Bywa zdublowany między podmiotami — NIGDY nie jest kluczem mapowania. */
      taxId: z.string().max(50).nullable(),
    }),
  ),
  fields: z.object({
    contractor: z.array(fieldSchema),
    employee: z.array(fieldSchema),
    contractor_contact: z.array(fieldSchema),
  }),
  dictionaries: z.array(dictionarySchema),
  /** Liczby rekordów — wdrożeniowiec od razu widzi skalę i czy baza w ogóle jest używana. */
  counts: z.object({
    contractor: z.number().int().nonnegative(),
    employee: z.number().int().nonnegative(),
    contractor_contact: z.number().int().nonnegative(),
  }),
  /**
   * Co program zauważył, a co nie zatrzymuje rozpoznania: brakujący typ obiektu, pole
   * z definicją bez kolumny, procedura o nieoczekiwanym kształcie. Trafia na ekran, żeby
   * wdrożeniowiec zobaczył to przed uruchomieniem przebiegu, a nie po nim.
   */
  warnings: z.array(z.string().max(500)).default([]),
});

export type LegacySourceSchemaPayload = z.infer<typeof legacySourceSchemaPayloadSchema>;

/**
 * Pola, które realnie da się wskazać po lewej stronie mapowania.
 *
 * Odpadają przyciski (nie mają kolumny, więc nie niosą wartości). Reszta zostaje, także pola
 * wskazujące na inne rekordy — te wymagają drugiego przebiegu, ale wdrożeniowiec ma je
 * zobaczyć i zmapować od razu.
 */
export function mappableFields(fields: LegacySourceField[]): LegacySourceField[] {
  return fields.filter((f) => f.type !== 'button');
}
